import { Link } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useProvinceEventFeedUnseen } from '../../hooks/useProvinceEventFeed';
import { SEVERITY_STYLE } from '../../utils/provinceEventSeverity';
import { COLORS } from '../../constants/theme';

interface IncomingActivitySectionProps {
  /** Per-platform cap (RECENT_ACTIVITY_CAP_DESKTOP=5 / _MOBILE=3), also the hook's fetch limit. */
  limit: number;
}

/**
 * SF-123 Component 7: the Recent Activity card's "Incoming" subsection -- a read-only,
 * `unseen=true` fetch (Decision 7: Throne Room never calls mark-seen). Own independent loading/
 * error/empty states, not exclusive with the sibling "Outgoing" subsection (SF-151 convention).
 */
export function IncomingActivitySection({ limit }: IncomingActivitySectionProps) {
  const { worldId, provinceId } = useAuth();
  const { events, isLoading, error } = useProvinceEventFeedUnseen(worldId, provinceId, limit);

  return (
    <div>
      <h3
        className="text-xs font-semibold uppercase tracking-wide mb-1"
        style={{ color: COLORS.text.tertiary, letterSpacing: '0.06em' }}
      >
        Incoming
      </h3>

      {isLoading && (
        <div className="flex flex-col gap-1" aria-hidden="true" data-testid="incoming-activity-skeleton">
          {Array.from({ length: Math.min(limit, 3) }).map((_, idx) => (
            <div
              key={idx}
              className="h-4 rounded"
              style={{ backgroundColor: COLORS.sectionHeader }}
            />
          ))}
        </div>
      )}

      {!isLoading && error && (
        <div className="text-sm" style={{ color: COLORS.text.tertiary }}>
          Unable to load recent activity.
        </div>
      )}

      {!isLoading && !error && events.length === 0 && (
        <div className="text-sm" style={{ color: COLORS.positive }}>
          No incoming activity — you're safe for now.
        </div>
      )}

      {!isLoading && !error && events.length > 0 && (
        <div className="flex flex-col gap-1">
          {events.map((event) => {
            const { color, glyph } = SEVERITY_STYLE[event.severity];
            return (
              <div key={event.eventSeq} className="text-sm flex items-start gap-1.5">
                <span aria-hidden="true" style={{ color, flexShrink: 0 }}>
                  {glyph}
                </span>
                <span style={{ color }}>{event.summary}</span>
              </div>
            );
          })}
        </div>
      )}

      <Link to="/province/events" className="text-xs mt-1 inline-block" style={{ color: COLORS.link }}>
        View all
      </Link>
    </div>
  );
}
